// RequireAuth gates a route. Hydrates auth state on first mount; redirects
// to /login when the refresh attempt comes back anonymous.
//
// Optional `role` prop hides admin-only routes from non-admins (UI gate;
// the server still enforces). Uses the EFFECTIVE role so the StrataTweaks
// dev override works for preview without flipping real claims.
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useEffect } from "react";

import { useAuth, effectiveRole, isAdminRole, type GlobalRole } from "@/store/auth";

interface Props {
  role?: GlobalRole;
}

export function RequireAuth({ role }: Props) {
  const location = useLocation();
  const status = useAuth((s) => s.status);
  const hydrate = useAuth((s) => s.hydrate);
  // SUBSCRIBE to the effective role rather than snapshotting it via
  // useAuth.getState(). The snapshot version only re-evaluated on unrelated
  // re-renders, so if /v1/me re-fetched with a downgraded role mid-session
  // (admin revoked, membership removed), the guard kept rendering the
  // admin-only outlet until the next manual navigation. With the selector
  // the component re-renders the moment the role string changes and the
  // redirect fires immediately.
  const r = useAuth((s) => effectiveRole(s));
  // Subscribe to user too. For role-gated routes we need to know whether
  // /v1/me has populated the store before we evaluate the role. See the
  // refresh-redirect comment in the role-check branch below.
  const user = useAuth((s) => s.user);

  useEffect(() => {
    if (status === "unknown") {
      void hydrate();
    }
  }, [status, hydrate]);

  if (status === "unknown") {
    return <FullScreenLoading />;
  }

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (role) {
    // Deep-link refresh race: hydrate() flips status to "authenticated"
    // the moment the refresh-cookie exchange succeeds, but the /v1/me
    // fetch (which populates `user` via useMe in App.tsx) hasn't
    // resolved yet. Without the user-null guard below, an admin-gated
    // route renders with r=null, isAdminRole(null)=false, and we
    // Navigate to "/" before /v1/me ever finishes — kicking the operator
    // back to the dashboard on every F5. Wait for user to populate so
    // the role check evaluates against real data.
    //
    // Failure mode: if /v1/me errors with a non-401 (e.g. 500), user
    // stays null and this branch keeps rendering Loading. The 401 path
    // is handled by the response interceptor (flips status to anonymous
    // → next render shows /login). 5xx is an honest "page can't load"
    // state which a refresh + backend recovery resolves.
    if (!user) {
      return <FullScreenLoading />;
    }
    // An "admin" route also admits "superadmin" (platform super-role).
    const ok = role === "admin" ? isAdminRole(r) : r === role;
    if (!ok) {
      return <Navigate to="/" replace />;
    }
  }

  return <Outlet />;
}

function FullScreenLoading() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        color: "var(--ink-3)",
        fontSize: "var(--type-sm)",
      }}
    >
      Loading…
    </div>
  );
}
